import React, { useState, useEffect, useContext, useMemo } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { validateInappropriateLanguage } from '../../../utils/profanitySanitizer';

// Subcomponentes modulares
import WelcomeMessageHeader from './componentes/WelcomeMessageHeader';
import WelcomeMessageEditor from './componentes/WelcomeMessageEditor';
import WelcomeWhatsAppPreview from './componentes/WelcomeWhatsAppPreview';

const DEFAULT_FALLBACK_PREFIX = 'Hola. Gracias por comunicarte con nuestro equipo, en este momento te está atendiendo {{nombre_agente}}';

const AutomaticMessagesSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto, idUsuario } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [prefixText, setPrefixText] = useState(DEFAULT_FALLBACK_PREFIX);
    const [customMessage, setCustomMessage] = useState('');
    const [isPersonalized, setIsPersonalized] = useState(false);
    const [lastSavedText, setLastSavedText] = useState('');

    // Cargar mensaje exclusivo del asesor en sesión
    const fetchUserMessage = async () => {
        setIsLoading(true);
        try {
            const uId = idUsuario ? parseInt(idUsuario, 10) : '';
            const query = uId ? `?clave=BIENVENIDA&idUsuario=${uId}` : '?clave=BIENVENIDA';
            const response = await fetch(`/api/SupportEngine/GetMensajesAutomaticosByClave${query}`, {
                headers: {
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                }
            });

            if (response.ok) {
                const result = await response.json();
                const rawList = result.data || result || [];
                const list = Array.isArray(rawList) ? rawList : (rawList ? [rawList] : []);

                if (list.length > 0) {
                    const first = list[0];
                    if (first.vPrefijo && first.vPrefijo.trim()) {
                        setPrefixText(first.vPrefijo.trim());
                    }

                    // Determinar si el asesor tiene un mensaje personalizado explícito guardado
                    const hasUserAssigned = Boolean(first.idUsuario && Number(first.idUsuario) === Number(idUsuario));
                    setIsPersonalized(hasUserAssigned);

                    // Si el usuario tiene su propio mensaje guardado se carga, sino se deja vacío por defecto
                    const textVal = hasUserAssigned && first.vMensajePersonalizado ? first.vMensajePersonalizado.trim() : '';
                    setCustomMessage(textVal);
                    setLastSavedText(textVal);
                } else {
                    setCustomMessage('');
                    setLastSavedText('');
                    setIsPersonalized(false);
                }
            } else {
                if (showErrorToast) showErrorToast('No se pudo cargar la configuración de tu mensaje de bienvenida.');
            }
        } catch (err) {
            console.error('Error al cargar mensaje de bienvenida:', err);
            if (showErrorToast) showErrorToast('Error de conexión al cargar mensaje de bienvenida.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchUserMessage();
    }, [token, idUsuario]);

    // Validación de lenguaje inapropiado
    const profanityValidation = useMemo(() => {
        return validateInappropriateLanguage(customMessage);
    }, [customMessage]);

    // Limpiar mensaje
    const handleClear = () => {
        setCustomMessage('');
    };

    // Guardar exclusivamente para el asesor en sesión
    const handleSave = async () => {
        if (!profanityValidation.isValid) {
            if (showErrorToast) {
                showErrorToast(`No se puede guardar: El mensaje contiene una palabra no permitida ("${profanityValidation.foundWord}").`);
            }
            return;
        }

        const trimmed = customMessage.trim();

        setIsSaving(true);
        try {
            const payload = {
                idMensaje: 0,
                vClaveMensaje: 'BIENVENIDA',
                vJsonMensajes: JSON.stringify([
                    {
                        idUsuario: idUsuario ? parseInt(idUsuario, 10) : null,
                        vMensajePersonalizado: trimmed
                    }
                ]),
                vCreatedUser: nombreCompleto || 'Asesor',
                vUpdatedUser: nombreCompleto || 'Asesor'
            };

            const response = await fetch('/api/SupportEngine/CreateMensajeAutomatico', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                if (showSuccessToast) {
                    showSuccessToast('¡Tu saludo de bienvenida ha sido guardado exitosamente!');
                }
                setIsPersonalized(Boolean(trimmed));
                setLastSavedText(trimmed);
            } else {
                const errData = await response.json().catch(() => ({}));
                if (showErrorToast) showErrorToast(errData.message || 'Error al guardar tu saludo de bienvenida.');
            }
        } catch (err) {
            console.error('Error al guardar mensaje:', err);
            if (showErrorToast) showErrorToast('Error de conexión al guardar el mensaje.');
        } finally {
            setIsSaving(false);
        }
    };

    // Prefijo con el nombre real del asesor renderizado
    const previewPrefix = useMemo(() => {
        const agentName = nombreCompleto ? nombreCompleto.trim() : 'Asesor Akasia';
        return prefixText.replace(/\{\{nombre_agente\}\}/gi, agentName);
    }, [prefixText, nombreCompleto]);

    const hasChanges = customMessage.trim() !== lastSavedText.trim();
    const canSave = profanityValidation.isValid && !isSaving && hasChanges;

    return (
        <div className="container-fluid p-0 mb-4">
            {/* Cabecera Principal */}
            <WelcomeMessageHeader
                isDark={isDark}
                nombreCompleto={nombreCompleto}
                idUsuario={idUsuario}
            />

            {isLoading ? (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-primary fa-2x mb-3" />
                    <p className="text-muted small">Cargando tu configuración de bienvenida...</p>
                </div>
            ) : (
                <div className="row g-4">
                    {/* Columna Izquierda: Editor del Saludo Personal */}
                    <div className="col-12 col-xl-7">
                        <WelcomeMessageEditor
                            isDark={isDark}
                            prefixText={prefixText}
                            customMessage={customMessage}
                            setCustomMessage={setCustomMessage}
                            isPersonalized={isPersonalized}
                            profanityValidation={profanityValidation}
                            hasChanges={hasChanges}
                            canSave={canSave}
                            isSaving={isSaving}
                            onSave={handleSave}
                            onClear={handleClear}
                        />
                    </div>

                    {/* Columna Derecha: Vista Previa en Vivo WhatsApp */}
                    <div className="col-12 col-xl-5">
                        <WelcomeWhatsAppPreview
                            isDark={isDark}
                            nombreCompleto={nombreCompleto}
                            previewPrefix={previewPrefix}
                            customMessage={customMessage}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomaticMessagesSettingsSection;
